import { AxiosResponse } from 'axios'
import apiService from './index'

export const getAvailableIntegrations: () => Promise<AxiosResponse<any>> = async () => {
    return await apiService.get(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/available`)
}

export const getPurchasedIntegrations: () => Promise<AxiosResponse<any>> = async () => {
    return await apiService.get(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/purchased`)
}

export const getIntegrationUsers: (id: string) => Promise<AxiosResponse<any>> = async (integrationId: string) => {
    return await apiService.get(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/${integrationId}/users`)
}

export const connectIntegration: (id: string, payload: any) => Promise<AxiosResponse<any>> = async (integrationId: string, payload: any) => {
    return await apiService.post(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/${integrationId}/connect`, payload)
}

export const removeIntegration: (id: string) => Promise<AxiosResponse<any>> = async (integrationId: string) => {
    return await apiService.delete(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/${integrationId}/remove`)
}

export const grantAccess: (id: string, userId: string) => Promise<AxiosResponse<any>> = async (integrationId: string, userId: string) => {
    return await apiService.post(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/${integrationId}/users/${userId}/grant`)
}

export const revokeAccess: (id: string, userId: string) => Promise<AxiosResponse<any>> = async (integrationId: string, userId: string) => {
    return await apiService.delete(`${process.env.REACT_APP_BACKEND_URL_V3}/integrations/${integrationId}/users/${userId}/revoke`)
}

export const integrationSetup = async (
    payload: { organizationId?: string } = {},
    bearerToken?: string
): Promise<AxiosResponse<any>> => {
    // bearerToken is for unauthenticated contexts (e.g. signup checkout) where
    // getApiToken() is empty, so the axios request interceptor wouldn't attach
    // an Authorization header on its own.
    const config = bearerToken
        ? { headers: { Authorization: `Bearer ${bearerToken}` } }
        : undefined
    return apiService.post(
        `${process.env.REACT_APP_BACKEND_URL_V3}/integrations/billing/setup-payment`,
        payload,
        config
    )
}

const billingBase = `${process.env.REACT_APP_BACKEND_URL_V3}/integrations/billing`

const orgQuery = (organizationId?: string) =>
    organizationId ? `?organizationId=${organizationId}` : ''

export const getPaymentMethods = async (organizationId?: string): Promise<AxiosResponse<any>> =>
    apiService.get(`${billingBase}/payment-methods${orgQuery(organizationId)}`)

export const setPrimaryPaymentMethod = async (
    pmId: string,
    organizationId?: string,
    bearerToken?: string
): Promise<AxiosResponse<any>> => {
    // bearerToken is for unauthenticated contexts (e.g. signup checkout) where
    // getAuth() is empty, so the axios request interceptor wouldn't attach an
    // Authorization header. Pass the signup loginToken explicitly to call this
    // endpoint right after creating the initial subscription.
    const config = bearerToken
        ? { headers: { Authorization: `Bearer ${bearerToken}` } }
        : undefined
    return apiService.patch(
        `${billingBase}/payment-methods/${pmId}/primary${orgQuery(organizationId)}`,
        undefined,
        config
    )
}

export const deletePaymentMethod = async (
    pmId: string,
    organizationId?: string
): Promise<AxiosResponse<any>> =>
    apiService.delete(`${billingBase}/payment-methods/${pmId}${orgQuery(organizationId)}`)

export type InvoiceStatus = 'paid' | 'open' | 'void' | 'uncollectible' | 'draft'

export interface ListInvoicesParams {
    organizationId?: string
    from?: string
    to?: string
    status?: InvoiceStatus
    limit?: number
    cursor?: string
}

export const listInvoices = async (
    params: ListInvoicesParams = {}
): Promise<AxiosResponse<any>> => {
    const search = new URLSearchParams()
    if (params.organizationId) search.set('organizationId', params.organizationId)
    if (params.from) search.set('from', params.from)
    if (params.to) search.set('to', params.to)
    if (params.status) search.set('status', params.status)
    if (params.limit) search.set('limit', String(params.limit))
    if (params.cursor) search.set('cursor', params.cursor)
    const qs = search.toString()
    return apiService.get(`${billingBase}/invoices${qs ? `?${qs}` : ''}`)
}

export const resolveInvoicePdfUrl = async (
    invoiceId: string,
    organizationId?: string
): Promise<AxiosResponse<any>> =>
    apiService.get(`${billingBase}/invoices/${invoiceId}/pdf${orgQuery(organizationId)}`)
